import React, { useState } from 'react';
import { useAuth, API } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { User, CreditCard, Bell, Shield, Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../components/LanguageSelector';

const Settings = () => {
  const { user, token } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const tier = user?.subscription_tier || 'free';
  const billingCycle = user?.subscription_billing_cycle || 'N/A';
  const subscriptionStatus = user?.subscription_status || 'inactive';
  const isBetaUser = user?.is_beta_user || user?.beta_access || subscriptionStatus === 'beta';

  return (
    <DashboardLayout title={t('settings.title')}>
      <div className="max-w-3xl mx-auto space-y-8">
        {/* Language Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-premium p-8">
          <div className="flex items-center gap-3 mb-6">
            <Globe className="text-[#D4AF37]" size={24} />
            <h2 className="font-serif text-xl text-white">{t('settings.language')}</h2>
          </div>
          <LanguageSelector variant="settings" />
        </motion.div>

        {/* Profile Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="card-premium p-8">
          <div className="flex items-center gap-3 mb-6">
            <User className="text-[#E5C585]" size={24} />
            <h2 className="font-serif text-xl text-white">Profile</h2>
          </div>
          
          <div className="flex items-center gap-6 mb-6">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-20 h-20 rounded-full" />
            ) : (
              <div className="w-20 h-20 bg-[#1C1C1C] rounded-full flex items-center justify-center">
                <span className="text-[#E5C585] font-serif text-2xl">{user?.name?.charAt(0)}</span>
              </div>
            )}
            <div>
              <h3 className="text-white text-xl font-serif">{user?.name}</h3>
              <p className="text-[#A1A1AA]">{user?.email}</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <Label className="text-[#A1A1AA] text-sm mb-2 block">Full Name</Label>
              <Input
                value={user?.name || ''}
                disabled
                className="bg-[#0A0A0A] border border-[#27272A] text-white rounded-none"
              />
            </div>
            <div>
              <Label className="text-[#A1A1AA] text-sm mb-2 block">Email</Label>
              <Input
                value={user?.email || ''}
                disabled
                className="bg-[#0A0A0A] border border-[#27272A] text-white rounded-none"
              />
            </div>
          </div>
        </motion.div>

        {/* Subscription Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-premium p-8">
          <div className="flex items-center gap-3 mb-6">
            <CreditCard className="text-[#D4AF37]" size={24} />
            <h2 className="font-serif text-xl text-white">Subscription</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-6">
            <div className="bg-[#0A0A0A] p-4 border border-[#27272A]">
              <span className="text-[#52525B] text-xs font-accent tracking-wider">CURRENT PLAN</span>
              <p className="text-white font-serif text-xl mt-1 capitalize">{tier}</p>
            </div>
            <div className="bg-[#0A0A0A] p-4 border border-[#27272A]">
              <span className="text-[#52525B] text-xs font-accent tracking-wider">BILLING CYCLE</span>
              <p className="text-white font-serif text-xl mt-1 capitalize">{billingCycle}</p>
            </div>
            <div className="bg-[#0A0A0A] p-4 border border-[#27272A]">
              <span className="text-[#52525B] text-xs font-accent tracking-wider">STATUS</span>
              <p className="text-white font-serif text-xl mt-1 capitalize">{user?.subscription_status || 'Inactive'}</p>
            </div>
          </div>

          {!isBetaUser && (
            <Button onClick={() => window.location.href = '/my-subscription'} className="btn-outline-gold px-6 py-3" data-testid="manage-subscription">
              {tier === 'free' ? t('premium.upgradePlan') : t('premium.manageSubscription')}
            </Button>
          )}
        </motion.div>

        {/* Notifications Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card-premium p-8">
          <div className="flex items-center gap-3 mb-6">
            <Bell className="text-[#E5C585]" size={24} />
            <h2 className="font-serif text-xl text-white">Notifications</h2>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#27272A]">
              <div>
                <p className="text-white">Email Notifications</p>
                <p className="text-[#52525B] text-sm">Receive updates about your brand journey</p>
              </div>
              <div className="w-12 h-6 bg-[#D4AF37] rounded-full relative cursor-pointer">
                <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
              </div>
            </div>
            <div className="flex items-center justify-between p-4 bg-[#0A0A0A] border border-[#27272A]">
              <div>
                <p className="text-white">Challenge Reminders</p>
                <p className="text-[#52525B] text-sm">Get reminded about active challenges</p>
              </div>
              <div className="w-12 h-6 bg-[#D4AF37] rounded-full relative cursor-pointer">
                <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full" />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Privacy Section */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="card-premium p-8">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="text-[#D4AF37]" size={24} />
            <h2 className="font-serif text-xl text-white">Privacy & Security</h2>
          </div>

          <p className="text-[#A1A1AA] mb-6">
            Your data is secure and encrypted. We never share your personal information with third parties.
          </p>

          <div className="flex gap-4">
            <Button className="btn-outline-gold px-6 py-3 text-sm" data-testid="export-data">
              Export My Data
            </Button>
            <Button className="border border-red-500/30 text-red-400 hover:bg-red-500/10 px-6 py-3 text-sm" data-testid="delete-account">
              Delete Account
            </Button>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
